<script setup>
import { reactive, onMounted, ref, computed, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import OrdemCompraItensSmartMode from '@/components/Compras/ordensCompra/OrdemCompraItensSmartMode.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import RelatorioPreviewModal from '@/components/relatorios/RelatorioPreviewModal.vue'
import { criarConfigBuscaFornecedorDocumentoEntrada } from '@/search-definitions/fornecedorSearch'
import { criarConfigBuscaOperacaoCompra } from '@/search-definitions/operacaoCompraSearch'
import { criarConfigBuscaCondicaoPagamento } from '@/search-definitions/condicaoPagamentoSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { dataHojeBR, formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import { OC_STATUS } from '@/constants/OC_STATUS'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'
import ClausulasGrid from '@/components/clausulas/ClausulasGrid.vue'
import { CLAUSULAS_COM_APLIC } from '@/constants/CLAUSULAS_COM_APLIC'
import api from '@/services/api'
import { loteFabricacaoSearch } from '@/search-definitions/loteFabricacaoSearch'
import { useValidacao } from '@/composables/useValidacao'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idOrdemCompra: null,
      idOrganizacao: null,
      idFilial: null,
      idOperacao: null,
      numeroOrdem: '',
      idParceiroNegocioFornecedor: null,
      idLocalEstoque: null,
      lstMoedaPadrao: '',
      lstIncoterm: '',
      idCondicaoPagamento: null,
      dataEmissao: dataHojeBR(),
      dataPrevisaoEntrega: null,
      valorTotalOriginal: 0,
      valorTotalDesconto: 0,
      valorTotalNegociado: 0,
      valorTotalOriginalPadrao: null,
      valorTotalDescontoPadrao: null,
      valorTotalNegociadoPadrao: null,
      valorTotalOriginalGerencial: null,
      valorTotalDescontoGerencial: null,
      valorTotalNegociadoGerencial: null,
      lstStatusOrdemCompra: '',
      idGrupoAcessoAprovador: null,
      idUsuarioAprovador: null,
      resumoAprovacao: '',
      lstFormaPagamento: '',
      lstMoedaGerencial: null,
      taxaCambioGerencialPadrao: null,
      lstTipoTaxaConversao: null,
      dataAlvoBuscaCambio: null,
      dataReferenciaCambio: null,
      lstTaxaCambioStatus: null,
      lstFonteCambio: null,
      clausulasInicializadas: false,
      percentualTolerancia: null,
      itens: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel,
  initializeForm,
  errors
} = useFormulario(props, emit)

// ============================================================
// FUNÇÃO: Salvar Alterações (via @submit-continuar)
// ============================================================
const salvarAlteracoesOrdem = async () => {
  if (isLoading.value || isReadOnly.value || !isOrdemSaved.value) return

  const payload = {
    ...item.value,
    idParceiroNegocioFornecedor: item.value.idParceiroNegocioFornecedor ? Number(item.value.idParceiroNegocioFornecedor) : null,
    idFilial: item.value.idFilial ? Number(item.value.idFilial) : null,
    idOperacao: item.value.idOperacao ? Number(item.value.idOperacao) : null,
    idCondicaoPagamento: item.value.idCondicaoPagamento ? Number(item.value.idCondicaoPagamento) : null,
    idGrupoAcessoAprovador: item.value.idGrupoAcessoAprovador ? Number(item.value.idGrupoAcessoAprovador) : null,
    idUsuarioAprovador: item.value.idUsuarioAprovador ? Number(item.value.idUsuarioAprovador) : null,
    valorTotalOriginal: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalOriginal) || Number(i.valorUnitarioOriginal || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0),
    valorTotalDesconto: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalDesconto) || Number(i.valorUnitarioDesconto || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0),
    valorTotalNegociado: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalNegociado) || 0), 0)
  }

  try {
    const res = await api.put(`/OrdensCompra/${item.value.idOrdemCompra}`, payload)
    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const listas = reactive({})
const isLoadingListas = ref(false)
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const gruposAprovacao = ref([])
const aprovadoresDisponiveis = ref([])
const isLoadingAprovadores = ref(false)

const accordionState = reactive({
  capa: true,
  itens: false,
  moedaGerencial: false,
  documentosEntrada: false,
  clausulas: false
})

const { processarErrosBackend } = useValidacao({
  formErrors: errors,
  accordionState,
  fieldTabMap: {
    idFilial: 'capa',
    idOperacao: 'capa',
    idParceiroNegocioFornecedor: 'capa',
    idLocalEstoque: 'capa',
    lstMoedaPadrao: 'capa',
    lstIncoterm: 'capa',
    idCondicaoPagamento: 'capa',
    lstFormaPagamento: 'capa',
    idGrupoAcessoAprovador: 'capa',
    resumoAprovacao: 'capa',
    percentualTolerancia: 'capa',
    clausulas: 'clausulas',
    itens: 'itens',
    quantidadePedidaUnFornecedor: 'itens',
    valorUnitarioNegociado: 'itens'
  }
})

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)

// Helper de sequência do status da OC (Rascunho=1, EmAprovacao=2, Aprovada=3, ...)
const seqStatus = (codigo) => OC_STATUS.sequencia[codigo] || 0

// Bloqueio por sequência: a partir de EmAprovacao (seq >= 2) a edição de dados é bloqueada
const statusBloqueadoEdicao = computed(() => {
  const status = item.value.lstStatusOrdemCompra
  return status && seqStatus(status) >= seqStatus(OC_STATUS.EmAprovacao)
})

// ReadOnly: perfil sem permissão OU status bloqueado por sequência
const isReadOnly = computed(() => {
  if (!isEditing.value) return false
  if (!temPermissaoCompleta.value) return true
  return statusBloqueadoEdicao.value
})

// Status terminais: Encerrada, EncerradaParcialmente, Cancelada, Reprovada
const isStatusTerminal = computed(() => {
  const status = item.value.lstStatusOrdemCompra
  if (!status) return false
  const seq = seqStatus(status)
  return seq >= seqStatus(OC_STATUS.EncerradaParcialmente) || status === OC_STATUS.Cancelada || status === OC_STATUS.Reprovada
})

// Permite editar observação do item mesmo com OC bloqueada (apenas para quem tem permissão e status não terminal)
const permitirEdicaoObservacao = computed(() =>
  temPermissaoCompleta.value && isEditing.value && !isStatusTerminal.value
)

// Bloqueio dos campos de aprovação: segue isReadOnly (seq >= 2 ou sem permissão)
const isAprovacaoBloqueada = computed(() => isReadOnly.value)

const configBuscaFornecedor = computed(() => criarConfigBuscaFornecedorDocumentoEntrada(true))
const configBuscaOperacao = computed(() => criarConfigBuscaOperacaoCompra())
const configBuscaCondicaoPagamento = computed(() => criarConfigBuscaCondicaoPagamento())
const configBuscaLocalEstoque = computed(() => criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, item.value.idFilial, []))

const isOrdemSaved = ref(isEditing.value)
const isSavingOrdemLocal = ref(false)
// isItensFrameBlocked agora controla apenas a exibição (v-if/v-else), não bloqueia visualmente os cards
const isItensFrameBlocked = computed(() => !isOrdemSaved.value)

const documentoSelecionado = ref(null)
const temDocumentoEntrada = computed(() => authStore.temModulo('DOCUMENTO_ENTRADA'))

const showRelatorioModal = ref(false)
const templatesRelatorio = ref([])
const dadosRelatorio = ref(null)

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['OrdemCompra', 'OrdemCompraItem'])

    // Carrega listas de câmbio que não estão mapeadas diretamente para OrdemCompra
    const [listaTipoTaxa, listaStatusTaxa] = await Promise.all([
      metadataStore.getListaLookup('FIN_TP_TAX_CONVERT', 'Cliente'),
      metadataStore.getListaLookup('FIN_ST_TAX_CONVERCAO', 'Cliente')
    ])
    result.LstTipoTaxaConversao = listaTipoTaxa
    result.LstTaxaCambioStatus = listaStatusTaxa

    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  } finally {
    isLoadingListas.value = false
  }
}

const carregarMoedaPadraoEmpresa = async () => {
  if (item.value.lstMoedaPadrao) return
  try {
    const res = await api.get(`/Empresas/${authStore.empresaAtiva}`)
    if (res.data && res.data.lstMoedaPadrao) {
      item.value.lstMoedaPadrao = res.data.lstMoedaPadrao
    }
  } catch (e) {
    console.warn('Não foi possível carregar a moeda padrão da empresa:', e)
  }
}

// Chave de permissão granular de aprovação da Ordem de Compra
const CHAVE_PERMISSAO_APROVACAO_OC = 'APROVACAO_OC_APROVAR'

// Grupos de aprovação (padrão PermissoesConsulta)
const carregarGruposAprovacao = async () => {
  try {
    let url = `/PermissoesConsulta/grupos?chave=${encodeURIComponent(CHAVE_PERMISSAO_APROVACAO_OC)}`
    if (authStore.isAdminInterno) url += `&targetIdEmpresa=${authStore.empresaAtiva}`
    const response = await api.get(url)
    gruposAprovacao.value = Array.isArray(response.data) ? response.data : []
  } catch (e) {
    console.warn('Erro ao carregar grupos de aprovação:', e)
    gruposAprovacao.value = []
  }
}

// Aprovadores (padrão PermissoesConsulta):
// com grupo selecionado → usuários do grupo; sem grupo → todos os usuários com a permissão.
const carregarAprovadoresDisponiveis = async () => {
  const grupoId = item.value.idGrupoAcessoAprovador
  isLoadingAprovadores.value = true
  try {
    let response
    if (grupoId) {
      response = await api.get(`/PermissoesConsulta/grupos/${grupoId}/usuarios`)
    } else {
      response = await api.get('/PermissoesConsulta/usuarios', {
        params: { chave: CHAVE_PERMISSAO_APROVACAO_OC }
      })
    }
    aprovadoresDisponiveis.value = Array.isArray(response.data) ? response.data : []
  } catch (e) {
    aprovadoresDisponiveis.value = []
    emit('show-notification', { message: 'Erro ao carregar aprovadores disponíveis.', type: 'error' })
  } finally {
    isLoadingAprovadores.value = false
  }
}

const onGrupoAprovacaoChange = () => {
  item.value.idUsuarioAprovador = ''
  carregarAprovadoresDisponiveis()
}

const getTextoLista = (codigo, nomeLista) => {
  if (!codigo) return '-'
  const lista = listas[nomeLista]
  if (!lista) return codigo
  const itemLista = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return itemLista ? itemLista.textoLista : codigo
}

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaSistemaItem) === idStr ||
    String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

// Taxa de conversão da moeda padrão para a gerencial (calculada localmente para exibição)
const taxaCambioGerencialExibicao = computed(() => {
  const tgp = Number(item.value.taxaCambioGerencialPadrao) || 0
  if (!(tgp > 0)) return null
  return Math.round((1 / tgp) * 1e6) / 1e6
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `Taxa ${siglaPadrao}/${siglaGerencial}`
})

const customEntityInfo = computed(() => {
  if (!isEditing.value) return entityInfo.value
  return {
    id: entityInfo.value.id,
    name: item.value.numeroOrdem || 'Ordem #' + item.value.idOrdemCompra,
    nameLabel: 'Nº Ordem'
  }
})

const statusExibicao = computed(() => {
  return getTextoLista(item.value.lstStatusOrdemCompra, 'LstStatusOrdemCompra')
})

const salvarOrdemLocalmente = async () => {
  if (isReadOnly.value) return
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }
  isSavingOrdemLocal.value = true
  const payload = {
    ...item.value,
    idParceiroNegocioFornecedor: item.value.idParceiroNegocioFornecedor ? Number(item.value.idParceiroNegocioFornecedor) : null,
    idFilial: item.value.idFilial ? Number(item.value.idFilial) : null,
    idOperacao: item.value.idOperacao ? Number(item.value.idOperacao) : null,
    idCondicaoPagamento: item.value.idCondicaoPagamento ? Number(item.value.idCondicaoPagamento) : null,
    idGrupoAcessoAprovador: item.value.idGrupoAcessoAprovador ? Number(item.value.idGrupoAcessoAprovador) : null,
    idUsuarioAprovador: item.value.idUsuarioAprovador ? Number(item.value.idUsuarioAprovador) : null,
    valorTotalOriginal: 0,
    valorTotalDesconto: 0,
    valorTotalNegociado: 0,
    itens: []
  }
  try {
    const response = await api.post('/OrdensCompra', payload)
    item.value = response.data
    isOrdemSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idOrdemCompra}`
    emit('show-notification', { message: 'Ordem salva. Agora pode adicionar produtos.', type: 'success' })
    accordionState.itens = true
  } catch (error) {
    console.error('Erro ao salvar ordem:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar a ordem de compra.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingOrdemLocal.value = false
  }
}

const handleItensAtualizados = (novaLista) => {
  item.value.itens = novaLista
  console.log('[DEBUG handleItensAtualizados] itens=', novaLista.length, 'idOrdemCompra=', item.value.idOrdemCompra)
  const totalOriginal = novaLista.reduce((s, i) => s + (Number(i.valorTotalOriginal) || Number(i.valorUnitarioOriginal || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0)
  const totalDesconto = novaLista.reduce((s, i) => s + (Number(i.valorTotalDesconto) || Number(i.valorUnitarioDesconto || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0)
  const totalNegociado = novaLista.reduce((s, i) => s + (Number(i.valorTotalNegociado) || 0), 0)
  item.value.valorTotalOriginal = totalOriginal
  item.value.valorTotalDesconto = totalDesconto
  item.value.valorTotalNegociado = totalNegociado
  // SmartMode autônomo — não precisa mais recarregar totais consumidos aqui
}

const submitFormOrdem = async () => {
  if (isReadOnly.value) return
  const payload = {
    ...item.value,
    idParceiroNegocioFornecedor: item.value.idParceiroNegocioFornecedor ? Number(item.value.idParceiroNegocioFornecedor) : null,
    idFilial: item.value.idFilial ? Number(item.value.idFilial) : null,
    idOperacao: item.value.idOperacao ? Number(item.value.idOperacao) : null,
    idCondicaoPagamento: item.value.idCondicaoPagamento ? Number(item.value.idCondicaoPagamento) : null,
    idGrupoAcessoAprovador: item.value.idGrupoAcessoAprovador ? Number(item.value.idGrupoAcessoAprovador) : null,
    idUsuarioAprovador: item.value.idUsuarioAprovador ? Number(item.value.idUsuarioAprovador) : null,
    valorTotalOriginal: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalOriginal) || Number(i.valorUnitarioOriginal || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0),
    valorTotalDesconto: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalDesconto) || Number(i.valorUnitarioDesconto || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0),
    valorTotalNegociado: item.value.itens.reduce((s, i) => s + (Number(i.valorTotalNegociado) || 0), 0)
    // Itens já persistidos individualmente via OrdemCompraItensSmartMode
  }
  await submitForm('OrdensCompra', 'idOrdemCompra', payload)
}

const configBuscaLoteFabricacao = computed(() => ({
  ...loteFabricacaoSearch,
  titulo: 'Buscar Lote de Fabricação',
  filtrosFixos: {}
}))

const onAcaoDocumentoCompleta = async ({ tipoAcao, documento }) => {
  if (tipoAcao === 'excluir' && documentoSelecionado.value?.idDocumentoEntrada === documento.idDocumentoEntrada) {
    documentoSelecionado.value = null
  }
  // O SmartMode agora é autônomo — não precisa mais recarregar totais consumidos aqui
}

/// Formata endereço a partir dos campos individuais da Filial (logradouro, numero, complemento, bairro, cidade, uf, cep)
const formatarEnderecoFilial = (filial) => {
  if (!filial) return ''
  const partes = []
  const linha1 = []
  if (filial.logradouro) linha1.push(filial.logradouro)
  if (filial.numero) linha1.push(filial.numero)
  if (filial.complemento) linha1.push(filial.complemento)
  if (linha1.length > 0) partes.push(linha1.join(', '))
  if (filial.bairro) partes.push(filial.bairro)
  const linhaCidade = []
  if (filial.cidade) linhaCidade.push(filial.cidade)
  if (filial.uf) linhaCidade.push(filial.uf)
  if (filial.cep) linhaCidade.push(filial.cep)
  if (linhaCidade.length > 0) partes.push(linhaCidade.join(' - '))
  return partes.join(', ') || ''
}

/// Formata endereço a partir dos campos individuais do LocalEstoque (logradouro, numero, complemento, bairro, cidade, uf, cep, pais)
const formatarEnderecoLocalEstoque = (local) => {
  if (!local) return ''
  const partes = []
  const linha1 = []
  if (local.logradouro) linha1.push(local.logradouro)
  if (local.numero) linha1.push(local.numero)
  if (local.complemento) linha1.push(local.complemento)
  if (linha1.length > 0) partes.push(linha1.join(', '))
  if (local.bairro) partes.push(local.bairro)
  const linhaCidade = []
  if (local.cidade) linhaCidade.push(local.cidade)
  if (local.uf) linhaCidade.push(local.uf)
  if (local.cep) linhaCidade.push(local.cep)
  if (linhaCidade.length > 0) partes.push(linhaCidade.join(' - '))
  if (local.pais) partes.push(local.pais)
  return partes.join(', ') || local.descricao || ''
}

const gerarRelatorio = async () => {
  const i = item.value
  if (!i.idOrdemCompra) {
    emit('show-notification', { message: 'Salve a Ordem de Compra antes de gerar o relatório.', type: 'warning' })
    return
  }
  try {
    // 1. Carrega templates
    const [templatesRes, habilitadosRes] = await Promise.all([
      api.get(`/RelatorioTemplates/por-modulo/ORDEM_COMPRA?idEmpresa=${authStore.empresaAtiva}`),
      api.get(`/EmpresaRelatorioTemplates/empresa/${authStore.empresaAtiva}`)
    ])

    const templates = (templatesRes.data || []).map(t => {
      const hab = (habilitadosRes.data || []).find(
        h => String(h.idRelatorioTemplate) === String(t.idRelatorioTemplate)
      )
      let imagensConfig = {}
      try {
        imagensConfig = hab?.configuracaoImagensJson ? JSON.parse(hab.configuracaoImagensJson) : {}
      } catch (e) { imagensConfig = {} }
      return { ...t, imagensConfig }
    })
    templatesRelatorio.value = templates
    if (templates.length === 0) {
      emit('show-notification', { message: 'Nenhum template de relatório disponível para Ordem de Compra. Contate o administrador.', type: 'warning' })
      return
    }

    // 2. Busca dados complementares para o relatório
    let compradorData = {}
    let fornecedorData = {}
    let clausulasData = []
    let condicaoPagamentoData = {}
    let nomeStatus = ''

    // 2a. Dados do comprador (Organização + Filial + Local de Estoque)
    try {
      const orgRes = await api.get(`/Organizacoes/${authStore.organizacaoAtiva}`)
      const org = orgRes.data || {}
      compradorData = {
        nome: org.nomeFantasia || org.razaoSocial || '',
        razaoSocial: org.razaoSocial || '',
        nomeFilial: i.nomeFilial || ''
      }
      // Dados fiscais e endereço da Filial
      if (i.idFilial) {
        try {
          const filialRes = await api.get(`/Filiais/${i.idFilial}`)
          const filial = filialRes.data || {}
          compradorData.cnpj = filial.cnpj || ''
          compradorData.inscricaoEstadual = filial.inscricaoEstadual || ''
          compradorData.endereco = formatarEnderecoFilial(filial)
          compradorData.cidade = filial.cidade || ''
          compradorData.uf = filial.uf || ''
          compradorData.cep = filial.cep || ''
          compradorData.telefone = filial.telefone || ''
        } catch (e) {
          // Fallback: dados da filial não disponíveis
        }
      }
      // Endereço do local de estoque (local de entrega)
      if (i.idLocalEstoque) {
        try {
          const localRes = await api.get(`/LocaisEstoque/${i.idLocalEstoque}`)
          const local = localRes.data || {}
          compradorData.enderecoLocalEstoque = formatarEnderecoLocalEstoque(local)
        } catch (e) {
          compradorData.enderecoLocalEstoque = ''
        }
      } else {
        compradorData.enderecoLocalEstoque = ''
      }
    } catch (e) {
      compradorData = { nome: '', razaoSocial: '', nomeFilial: '', enderecoLocalEstoque: '' }
    }

    // 2b. Dados do fornecedor (parceiro negócio)
    if (i.idParceiroNegocioFornecedor) {
      try {
        const fornRes = await api.get(`/Parceiros/${i.idParceiroNegocioFornecedor}`)
        const forn = fornRes.data || {}
        console.log('[DEBUG RELATORIO] forn data:', JSON.stringify(forn, null, 2))
        // Extrai o primeiro endereço principal
        const enderecos = Array.isArray(forn.enderecos) ? forn.enderecos : []
        console.log('[DEBUG RELATORIO] enderecos count:', enderecos.length, JSON.stringify(enderecos))
        const endPrincipal = enderecos.find(e => e.isPrincipal) || enderecos[0] || {}
        fornecedorData = {
          nome: forn.nomeFantasia || forn.nome || i.nomeFornecedor || '',
          razaoSocial: forn.razaoSocial || '',
          documento: forn.documento || '',
          inscricaoEstadual: forn.inscricaoEstadual || '',
          endereco: [endPrincipal.logradouro, endPrincipal.numero, endPrincipal.complemento].filter(Boolean).join(', ') || '',
          bairro: endPrincipal.bairro || '',
          cidade: endPrincipal.cidade || '',
          uf: endPrincipal.uf || '',
          cep: endPrincipal.cep || '',
          email: forn.emailPrincipal || '',
          telefone: forn.telFixo || forn.telCelular || ''
        }
      } catch (e) {
        fornecedorData = {
          nome: i.nomeFornecedor || `Fornecedor #${i.idParceiroNegocioFornecedor}`,
          razaoSocial: '',
          documento: '',
          inscricaoEstadual: '',
          endereco: '',
          cidade: '',
          uf: '',
          cep: '',
          email: '',
          telefone: ''
        }
      }
    }

    // 2c. Cláusulas da Ordem de Compra
    try {
      const clausulasRes = await api.get(`/OrdemCompraClausulas/por-ordem-compra/${i.idOrdemCompra}`)
      clausulasData = Array.isArray(clausulasRes.data) ? clausulasRes.data : []
    } catch (e) {
      clausulasData = i.clausulas || []
    }

    // 2d. Condição de Pagamento
    if (i.idCondicaoPagamento) {
      try {
        const condRes = await api.get(`/CondicoesPagamento/${i.idCondicaoPagamento}`)
        const cond = condRes.data || {}
        condicaoPagamentoData = {
          descricao: cond.descricao || i.nomeCondicaoPagamento || '',
          quantidadeParcelas: Number(cond.quantidadeParcelas) || 1,
          diasIntervalo: Number(cond.diasIntervalo) || 0,
          entrada: !!cond.entrada
        }
      } catch (e) {
        condicaoPagamentoData = {
          descricao: i.nomeCondicaoPagamento || '',
          quantidadeParcelas: 1,
          diasIntervalo: 0,
          entrada: false
        }
      }
    }

    // 2e. Resolve textos das listas (Lst*)
    nomeStatus = getTextoLista(i.lstStatusOrdemCompra, 'LstStatusOrdemCompra') || i.lstStatusOrdemCompra || ''
    const nomeMoeda = getTextoLista(i.lstMoedaPadrao, 'LstMoeda') || i.lstMoedaPadrao || ''
    const nomeFormaPagamento = getTextoLista(i.lstFormaPagamento, 'LstFormaPagamento') || i.lstFormaPagamento || ''
    const nomeIncoterm = i.lstIncoterm ? (getTextoLista(i.lstIncoterm, 'LstIncoterm') || i.lstIncoterm) : null

    // 3. Monta o objeto dadosRelatorio
    dadosRelatorio.value = {
      // Identificação
      numeroOrdem: i.numeroOrdem || '',
      idOrdemCompraOriginal: i.idOrdemCompraOriginal || null,
      numeroOrdemOriginal: i.numeroOrdemOriginal || null,
      lstStatusOrdemCompra: i.lstStatusOrdemCompra || '',
      nomeStatus: nomeStatus,

      // Comprador — Organização + Filial + Local de Estoque
      comprador: {
        nome: compradorData.nome || '',
        razaoSocial: compradorData.razaoSocial || '',
        cnpj: compradorData.cnpj || '',
        inscricaoEstadual: compradorData.inscricaoEstadual || '',
        endereco: compradorData.endereco || '',
        cidade: compradorData.cidade || '',
        uf: compradorData.uf || '',
        cep: compradorData.cep || '',
        telefone: compradorData.telefone || '',
        nomeFilial: compradorData.nomeFilial || i.nomeFilial || ''
      },

      // Fornecedor — endereço logístico
      fornecedor: {
        nome: fornecedorData.nome || i.nomeFornecedor || `Fornecedor #${i.idParceiroNegocioFornecedor}`,
        razaoSocial: fornecedorData.razaoSocial || '',
        documento: fornecedorData.documento || '',
        inscricaoEstadual: fornecedorData.inscricaoEstadual || '',
        endereco: fornecedorData.endereco || '',
        cidade: fornecedorData.cidade || '',
        uf: fornecedorData.uf || '',
        cep: fornecedorData.cep || '',
        email: fornecedorData.email || '',
        telefone: fornecedorData.telefone || ''
      },

      // Datas
      dataEmissao: formatarDataBR(i.dataEmissao),
      dataPrevisaoEntrega: formatarDataBR(i.dataPrevisaoEntrega),

      // Dados da Ordem
      nomeOperacao: i.nomeOperacao || '',
      nomeFilial: i.nomeFilial || '',
      lstIncoterm: i.lstIncoterm || null,
      nomeIncoterm: nomeIncoterm,
      localEntrega: compradorData.enderecoLocalEstoque || '',

      // Comercial
      lstMoeda: i.lstMoedaPadrao || '',
      nomeMoeda: nomeMoeda,
      lstFormaPagamento: i.lstFormaPagamento || '',
      nomeFormaPagamento: nomeFormaPagamento,
      nomeCondicaoPagamento: i.nomeCondicaoPagamento || '',
      condicaoPagamento: condicaoPagamentoData,

      // Itens da Ordem (expandido)
      produtos: (i.itens || []).map(p => ({
        nome: p.nomeProduto || '',
        sku: p.sku || '',
        codigoNcm: p.codigoNcm || null,
        quantidade: Number(p.quantidadePedidaUnFornecedor) || 0,
        unidade: p.lstUnidadeMedidaFornecedor || '',
        nomeUnidade: getTextoLista(p.lstUnidadeMedidaFornecedor, 'LstUnidadeMedidaFornecedor') || p.lstUnidadeMedidaFornecedor || '',
        // Finalidade da mercadoria (SPED)
        lstTipoItemSped: p.lstTipoItemSped || null,
        nomeTipoItemSped: p.nomeTipoItemSped || getTextoLista(p.lstTipoItemSped, 'LstTipoItemSped') || p.lstTipoItemSped || '',
        // Breakdown completo
        valorUnitarioOriginal: Number(p.valorUnitarioOriginal) || null,
        valorUnitarioDesconto: Number(p.valorUnitarioDesconto) || null,
        valorUnitarioNegociado: Number(p.valorUnitarioNegociado) || 0,
        valorTotalOriginal: Number(p.valorTotalOriginal) || null,
        valorTotalDesconto: Number(p.valorTotalDesconto) || null,
        valorTotalNegociado: Number(p.valorTotalNegociado) || 0,
        percentualTolerancia: Number(p.percentualTolerancia) || null,
        loteFornecedor: p.numeroLoteFornecedor || null,
        observacao: p.observacao || null
      })),

      // Totais (multi-moeda)
      totais: {
        original: Number(i.valorTotalOriginal) || 0,
        desconto: Number(i.valorTotalDesconto) || 0,
        negociado: Number(i.valorTotalNegociado) || 0,
        // Moeda padrão
        originalPadrao: Number(i.valorTotalOriginalPadrao) || null,
        descontoPadrao: Number(i.valorTotalDescontoPadrao) || null,
        negociadoPadrao: Number(i.valorTotalNegociadoPadrao) || null,
        // Moeda gerencial
        originalGerencial: Number(i.valorTotalOriginalGerencial) || null,
        descontoGerencial: Number(i.valorTotalDescontoGerencial) || null,
        negociadoGerencial: Number(i.valorTotalNegociadoGerencial) || null
      },

      // Cláusulas (todas — externas e comerciais)
      clausulas: clausulasData.map(c => ({
        titulo: c.titulo || '',
        texto: c.texto || '',
        ordemExibicao: c.ordemExibicao || 0
      })),

      // Aprovação
      aprovacao: {
        resumo: i.resumoAprovacao || '',
        nomeAprovador: i.nomeAprovador || '',
        nomeGrupoAprovador: i.nomeGrupoAprovador || ''
      },

      // Auditoria
      auditoria: {
        nomeUsuarioCriacao: i.nomeUsuarioCriacao || '',
        emailUsuarioCriacao: i.emailUsuarioCriacao || '',
        tsCriacao: formatarDataHoraBR(i.tsCriacao)
      }
    }

    showRelatorioModal.value = true
  } catch (error) {
    console.error('Erro ao carregar templates:', error)
    emit('show-notification', { message: 'Erro ao carregar templates de relatório.', type: 'error' })
  }
}

// --- AÇÕES DE TRANSIÇÃO DE STATUS ---
const isLoadingAcao = ref(false)
const confirmModal = ref(null)
const acaoPendente = ref(null)
const mensagemConfirmacao = ref('Tem certeza que deseja realizar esta ação?')

const statusAtual = computed(() => item.value.lstStatusOrdemCompra)

// O grid de Documentos de Entrada fica visível a partir do status Emitida (seq >= 4)
const isDocumentoEntradaVisivel = computed(() => {
  const status = statusAtual.value
  return isOrdemSaved.value && status && seqStatus(status) >= seqStatus(OC_STATUS.Emitida)
})

// Bloqueio de edição do grid de Documentos de Entrada:
// a partir de EncerradaParcialmente (seq >= 6) o grid fica read-only
// (apenas consulta visualização via modal, sem criar/editar documentos)
const isDocumentoEntradaReadOnly = computed(() => {
  const status = statusAtual.value
  return status && seqStatus(status) >= seqStatus(OC_STATUS.EncerradaParcialmente)
})

const podeSolicitarAprovacao = computed(() =>
  isOrdemSaved.value && statusAtual.value === OC_STATUS.Rascunho && temPermissaoCompleta.value
)

const podeCancelar = computed(() =>
  isOrdemSaved.value &&
  seqStatus(statusAtual.value) > 0 &&
  seqStatus(statusAtual.value) < seqStatus(OC_STATUS.FaturadaParcialmente) &&
  temPermissaoCompleta.value
)

const podeEncerrar = computed(() =>
  isOrdemSaved.value &&
  statusAtual.value === OC_STATUS.FaturadaParcialmente &&
  temPermissaoCompleta.value
)

const podeEmitir = computed(() =>
  isOrdemSaved.value &&
  statusAtual.value === OC_STATUS.Aprovada &&
  temPermissaoCompleta.value
)

const podeRegenerarRelatorio = computed(() =>
  isOrdemSaved.value &&
  (statusAtual.value === OC_STATUS.Emitida || statusAtual.value === OC_STATUS.FaturadaParcialmente)
)

const podeRetornarRascunho = computed(() =>
  isOrdemSaved.value &&
  statusAtual.value === OC_STATUS.EmAprovacao &&
  temPermissaoCompleta.value
)

const retornarRascunho = () => {
  acaoPendente.value = 'retornarRascunho'
  mensagemConfirmacao.value = `Tem certeza que deseja retornar a Ordem de Compra ${item.value.numeroOrdem} para Rascunho? Você poderá editá-la e solicitar aprovação novamente.`
  confirmModal.value.show()
}

const executarAcao = async (endpoint, mensagemSucesso, mensagemErro) => {
  if (isLoadingAcao.value) return
  isLoadingAcao.value = true
  try {
    await api.post(`/OrdensCompra/${item.value.idOrdemCompra}${endpoint}`, { observacao: '' })
    // Recarrega a OC para refletir o novo status
    const response = await api.get(`/OrdensCompra/${item.value.idOrdemCompra}`)
    item.value = response.data
    emit('show-notification', { message: mensagemSucesso, type: 'success' })
  } catch (error) {
    const data = error.response?.data
    const msg = processarErrosBackend(data) || mensagemErro
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const solicitarAprovacao = () => executarAcao(
  '/solicitar-aprovacao',
  'Aprovação solicitada com sucesso.',
  'Erro ao solicitar aprovação.'
)

const confirmarCancelar = () => {
  acaoPendente.value = 'cancelar'
  mensagemConfirmacao.value = `Tem certeza que deseja cancelar a Ordem de Compra ${item.value.numeroOrdem}? Esta ação não poderá ser desfeita.`
  confirmModal.value.show()
}

const confirmarEncerrar = () => {
  acaoPendente.value = 'encerrar'
  mensagemConfirmacao.value = `Tem certeza que deseja encerrar manualmente a Ordem de Compra ${item.value.numeroOrdem}? O saldo pendente será perdido.`
  confirmModal.value.show()
}

const confirmarEmitir = () => {
  acaoPendente.value = 'emitir'
  mensagemConfirmacao.value = `Tem certeza que deseja emitir a Ordem de Compra ${item.value.numeroOrdem} para o fornecedor? Após emitida, o relatório PDF será gerado automaticamente.`
  confirmModal.value.show()
}

const onConfirmAcao = async (confirmado) => {
  if (!confirmado || !acaoPendente.value) {
    acaoPendente.value = null
    return
  }
  if (acaoPendente.value === 'cancelar') {
    await executarAcao('/cancelar', 'Ordem cancelada com sucesso.', 'Erro ao cancelar ordem.')
  } else if (acaoPendente.value === 'encerrar') {
    await executarAcao('/encerrar', 'Ordem encerrada manualmente com sucesso.', 'Erro ao encerrar ordem.')
  } else if (acaoPendente.value === 'emitir') {
    await executarAcao('/emitir', 'Ordem emitida com sucesso.', 'Erro ao emitir ordem.')
    // Após emitir, gera o relatório PDF automaticamente
    if (item.value.lstStatusOrdemCompra === OC_STATUS.Emitida) {
      await gerarRelatorio()
    }
  } else if (acaoPendente.value === 'retornarRascunho') {
    await executarAcao('/retornar-rascunho', 'Ordem retornada para Rascunho com sucesso.', 'Erro ao retornar ordem para Rascunho.')
  }
  acaoPendente.value = null
}

onMounted(() => {
  console.log('[DEBUG OrdemCompraCadastro] onMounted. idOrdemCompra=', item.value.idOrdemCompra, 'itens.length=', (item.value.itens || []).length)
  carregarListas()
  carregarMoedaPadraoEmpresa()
  carregarGruposAprovacao()
  // Carrega aprovadores: do grupo selecionado ou todos os que possuem a permissão
  carregarAprovadoresDisponiveis()
})

// SmartMode autônomo — não precisa mais carregar totais consumidos aqui

watch(() => props.itemParaEditar?.idOrdemCompra, (novoId) => {
  if (novoId) {
    documentoSelecionado.value = null
  }
})

</script>

<template>
  <FormularioCadastro
    module-name="Ordem de Compra"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="customEntityInfo"
    :status="statusExibicao"
    :accordion-state="accordionState"
    :errors="errors"
    :desabilitar-salvar="isReadOnly"
    :ocultar-salvar="!isOrdemSaved"
    :desabilitar-salvar-alteracoes="isItensFrameBlocked"
    label-botao-adicionar="Salvar Ordem"
    @submit="submitFormOrdem"
    @submit-continuar="salvarAlteracoesOrdem"
    @cancelar="handleCancel"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- BARRA DE AÇÕES DA ORDEM DE COMPRA -->
    <div v-if="isOrdemSaved" class="d-flex justify-content-between align-items-center gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="podeRetornarRascunho"
          type="button"
          class="btn btn-sm btn-outline-warning fw-bold"
          :disabled="isLoadingAcao"
          @click="retornarRascunho"
        >
          <i class="bi bi-arrow-return-left me-1"></i>
          Retornar para Rascunho
        </button>
        <button
          v-if="podeCancelar"
          type="button"
          class="btn btn-sm btn-outline-danger fw-bold"
          :disabled="isLoadingAcao"
          @click="confirmarCancelar"
        >
          <i class="bi bi-x-lg me-1"></i>
          Cancelar
        </button>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="podeRegenerarRelatorio"
          type="button"
          class="btn btn-sm btn-outline-info fw-bold"
          :disabled="isLoadingAcao"
          @click="gerarRelatorio"
        >
          <i class="bi bi-file-pdf me-1"></i>
          Regenerar Relatório
        </button>
        <button
          v-if="podeSolicitarAprovacao"
          type="button"
          class="btn btn-sm btn-outline-primary fw-bold"
          :disabled="isLoadingAcao"
          @click="solicitarAprovacao"
        >
          <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-send-check me-1"></i>
          Solicitar Aprovação
        </button>
        <button
          v-if="podeEmitir"
          type="button"
          class="btn btn-sm btn-outline-success fw-bold"
          :disabled="isLoadingAcao"
          @click="confirmarEmitir"
        >
          <i class="bi bi-send me-1"></i>
          Emitir OC
        </button>
        <button
          v-if="podeEncerrar"
          type="button"
          class="btn btn-sm btn-outline-success fw-bold"
          :disabled="isLoadingAcao"
          @click="confirmarEncerrar"
        >
          <i class="bi bi-check2-square me-1"></i>
          Encerrar OC Parcialmente
        </button>
      </div>
    </div>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.capa = !accordionState.capa">
        <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Capa do Documento</h5>
        <div>
          <span v-if="isOrdemSaved" class="badge bg-success text-white me-2 fs-6"><i class="bi bi-check-lg"></i> Ordem #{{ item.idOrdemCompra }}</span>
          <i :class="['bi', accordionState.capa ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>
      <div class="card-body" v-show="accordionState.capa">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput id="numeroOrdem" label="Nº Ordem" v-model="item.numeroOrdem" required disabled :descricao-informativa="!item.numeroOrdem ? 'Gerado automaticamente pelo sistema' : undefined" />
            <div v-if="item.idOrdemCompraOriginal" class="mt-1">
              <span class="badge badge-ghost-warning">
                <i class="bi bi-file-earmark-plus me-1"></i>
                Revisão da {{ item.numeroOrdemOriginal || `OC #${item.idOrdemCompraOriginal}` }}
              </span>
            </div>
          </div>
          <div class="col-md-3">
            <CampoBusca id="idOperacao" v-model="item.idOperacao" :configuracao-busca="configBuscaOperacao" label="Operação" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoBusca id="idFilial" v-model="item.idFilial" :configuracao-busca="filialSearch" label="Filial" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoBusca id="idLocalEstoque" v-model="item.idLocalEstoque" :configuracao-busca="configBuscaLocalEstoque" label="Local Estoque" :disabled="isReadOnly" />
          </div>
          <div class="col-md-4">
            <CampoBusca id="idParceiroNegocioFornecedor" v-model="item.idParceiroNegocioFornecedor" :configuracao-busca="configBuscaFornecedor" label="Fornecedor" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoInput id="dataEmissao" label="Data Emissão" type="date" v-model="item.dataEmissao" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoBuscaLista id="lstIncoterm" label="Incoterm" v-model="item.lstIncoterm" tabela="OrdemCompra" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoInput id="dataPrevisaoEntrega" label="Previsão Entrega" type="date" v-model="item.dataPrevisaoEntrega" :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoInput id="percentualTolerancia" label="Tolerância Quantitativa (%)" type="percentual" v-model="item.percentualTolerancia" :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoBusca id="idCondicaoPagamento" v-model="item.idCondicaoPagamento" :configuracao-busca="configBuscaCondicaoPagamento" label="Cond. Pagamento" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista id="lstFormaPagamento" label="Forma Pagamento" v-model="item.lstFormaPagamento" tabela="OrdemCompra" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-2">
            <CampoSelect id="lstStatusOrdemCompra" label="Status" v-model="item.lstStatusOrdemCompra" :options="listas.LstStatusOrdemCompra || []" option-label="textoLista" option-value="idListaClienteItem" required disabled />
          </div>
        </div>

        <!-- Breakdown de Valores -->
        <div class="row g-3 mt-1 pt-2 border-top border-secondary-subtle">
          <div class="col-12">
            <h6 class="text-body-emphasis fw-bold mb-0"><i class="bi bi-calculator me-2 text-primary"></i> Breakdown de Valores</h6>
          </div>
          <div class="col-md-3">
            <CampoBuscaLista id="lstMoedaPadrao" label="Moeda Padrão" v-model="item.lstMoedaPadrao" tabela="OrdemCompra" required :disabled="true" />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorTotalOriginal" label="Valor Original" type="moeda" :model-value="item.itens.reduce((s, i) => s + (Number(i.valorTotalOriginal) || Number(i.valorUnitarioOriginal || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0)" disabled />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorTotalDesconto" label="Valor Desconto" type="moeda" :model-value="item.itens.reduce((s, i) => s + (Number(i.valorTotalDesconto) || Number(i.valorUnitarioDesconto || 0) * Number(i.quantidadePedidaUnFornecedor || 0)), 0)" disabled />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorTotalNegociado" label="Valor Negociado" type="moeda" :model-value="item.itens.reduce((s, i) => s + (Number(i.valorTotalNegociado) || 0), 0)" disabled />
          </div>
        </div>

        <!-- Sub-bloco Aprovação da Ordem -->
        <div class="row g-3 mt-1 pt-3 border-top border-secondary-subtle">
          <div class="col-12">
            <h6 class="text-body-emphasis fw-bold mb-0"><i class="bi bi-person-check me-2 text-primary"></i> Aprovação</h6>
          </div>

          <!-- Observação de reprovação (exibida quando a OC foi reprovada) -->
          <div v-if="item.lstStatusOrdemCompra === OC_STATUS.Reprovada && item.observacaoReprovacao" class="col-12">
            <div class="alert alert-danger border-0 rounded-3 d-flex align-items-start gap-2 mb-0 py-2">
              <i class="bi bi-x-octagon-fill fs-5 flex-shrink-0 mt-1"></i>
              <div>
                <strong class="d-block small">Motivo da Reprovação</strong>
                <span class="small">{{ item.observacaoReprovacao }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="idGrupoAcessoAprovador"
              label="Grupo de Aprovação"
              v-model="item.idGrupoAcessoAprovador"
              :options="gruposAprovacao"
              option-label="nome"
              option-value="idGrupoAcesso"
              required
              :disabled="isAprovacaoBloqueada"
              @update:modelValue="onGrupoAprovacaoChange"
            />
          </div>
          <div class="col-md-4">
            <CampoSelect
              id="idUsuarioAprovador"
              label="Aprovador"
              v-model="item.idUsuarioAprovador"
              :options="aprovadoresDisponiveis"
              option-label="nome"
              option-value="idUsuario"
              placeholder="Qualquer membro do grupo"
              :disabled="isAprovacaoBloqueada || isLoadingAprovadores"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="resumoAprovacao"
              label="Resumo da Ordem"
              type="textarea"
              v-model="item.resumoAprovacao"
              required
              maxlength="300"
              descricao-informativa="Exibido ao aprovador na tela de aprovações"
              :disabled="isAprovacaoBloqueada"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4" v-if="!isOrdemSaved">
      <button type="button" class="btn btn-success px-4" @click="salvarOrdemLocalmente" :disabled="isSavingOrdemLocal || !item.idFilial || !item.idOperacao || !item.idParceiroNegocioFornecedor || !item.idCondicaoPagamento">
        <span v-if="isSavingOrdemLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Ordem e Liberar Produtos
      </button>
    </div>

    <div>
      <div v-if="!isOrdemSaved" class="text-center text-muted py-4">
        <i class="bi bi-info-circle me-2"></i>
        Salve a ordem primeiro para adicionar produtos.
      </div>
      <OrdemCompraItensSmartMode
        v-else
        :itens="item.itens || []"
        :listas="listas"
        :pode-editar="!isReadOnly"
        :permitir-edicao-observacao="permitirEdicaoObservacao"
        :lst-moeda-padrao-ordem="item.lstMoedaPadrao"
        :id-parceiro-negocio-fornecedor="item.idParceiroNegocioFornecedor"
        :id-ordem-compra="item.idOrdemCompra"
        :accordion-aberto="accordionState.itens"
        :percentual-tolerancia-cabecalho="item.percentualTolerancia"
        @update:itens="handleItensAtualizados"
        @update:accordion-aberto="accordionState.itens = $event"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </div>

    <div v-if="isOrdemSaved" class="mb-3">
      <ClausulasGrid
        ref="clausulasGridRef"
        :id-documento="item.idOrdemCompra"
        tipo-documento="OC"
        :lst-aplicacao-clausula="CLAUSULAS_COM_APLIC.OC"
        :pode-editar="!isReadOnly"
        :ids-produtos="(item.itens || []).map(i => ({ idProduto: i.idProduto, sku: i.sku, nomeProduto: i.nomeProduto }))"
        :ids-parceiros="item.idParceiroNegocioFornecedor ? [{ idParceiro: item.idParceiroNegocioFornecedor, nomeParceiro: item.nomeFornecedor || item.razaoSocialFornecedor }] : []"
        :clausulas-inicializadas="item.clausulasInicializadas"
        :com-accordion="true"
        :accordion-aberto="accordionState.clausulas"
        cor-header-class="bg-body-tertiary"
        @update:accordion-aberto="accordionState.clausulas = $event"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </div>
    <div v-else class="text-center py-4 text-muted mb-3">
      <i class="bi bi-info-circle me-2"></i> Salve a ordem primeiro para gerenciar cláusulas.
    </div>

    <div>
      <div class="card mb-4" v-if="isEditing">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.moedaGerencial = !accordionState.moedaGerencial">
          <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Câmbio e Conversão</h5>
          <i :class="['bi', accordionState.moedaGerencial ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.moedaGerencial">
          <div v-if="!isOrdemSaved" class="text-center text-muted py-4">
            <i class="bi bi-info-circle me-2"></i>
            Salve a ordem primeiro para configurar a moeda gerencial.
          </div>
          <template v-else>
            <!-- Linha de cabeçalho do câmbio -->
            <div class="row g-3 mb-4 pb-3 border-bottom">
              <div class="col-md-3">
                <CampoBuscaLista id="lstTipoTaxaConversao" label="Tipo Taxa Conversão" v-model="item.lstTipoTaxaConversao" :options="listas.LstTipoTaxaConversao || []" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
              </div>
              <div class="col-md-2">
                <CampoInput id="dataReferenciaCambio" label="Data Ref. Câmbio" type="date" v-model="item.dataReferenciaCambio" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
              </div>
              <div class="col-md-2">
                <CampoInput id="dataAlvoBuscaCambio" label="Data Alvo Taxa" type="date" v-model="item.dataAlvoBuscaCambio" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
              </div>
              <div class="col-md-2">
                <CampoBuscaLista id="lstFonteCambio" label="Fonte da Cotação" v-model="item.lstFonteCambio" tabela="OrdemCompra" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
              </div>
              <div class="col-md-3">
                <CampoBuscaLista id="lstTaxaCambioStatus" label="Status Taxa" v-model="item.lstTaxaCambioStatus" :options="listas.LstTaxaCambioStatus || []" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
              </div>
            </div>

            <!-- Mini-cards: Moeda Padrão vs Moeda Gerencial -->
            <div class="row g-3">
              <div class="col-md-6">
                <div class="card border-primary h-100">
                  <div class="card-header bg-primary text-white py-2">
                    <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                  </div>
                  <div class="card-body py-2">
                    <div class="row g-2">
                      <div class="col-4">
                        <CampoBuscaLista id="lstMoedaPadrao" label="Moeda" v-model="item.lstMoedaPadrao" tabela="OrdemCompra" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
                      </div>
                      <div class="col-4">
                        <CampoInput id="taxaCambioPadrao" label="Taxa Câmbio" type="moeda" :casasDecimais="4" :model-value="1" disabled descricao-informativa="A OC é emitida na moeda padrão (taxa = 1)" />
                      </div>
                      <div class="col-4">
                        <CampoInput id="valorTotalNegociado" label="Valor Total" type="moeda" :model-value="item.valorTotalNegociado" disabled />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border-primary h-100">
                  <div class="card-header bg-primary text-white py-2">
                    <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
                  </div>
                  <div class="card-body py-2">
                    <div class="row g-2">
                      <div class="col-3">
                        <CampoBuscaLista id="lstMoedaGerencial" label="Moeda" v-model="item.lstMoedaGerencial" tabela="OrdemCompra" disabled descricao-informativa="Campos de conversão são gerados automaticamente" />
                      </div>
                      <div class="col-3">
                        <CampoInput id="taxaCambioGerencialPadrao" :label="labelTaxaGerencialPadrao" type="moeda" :casasDecimais="4" :model-value="item.taxaCambioGerencialPadrao" disabled />
                      </div>
                      <div class="col-3">
                        <CampoInput id="taxaCambioGerencial" :label="labelTaxaCambioGerencial" type="moeda" :casasDecimais="4" :model-value="taxaCambioGerencialExibicao" disabled />
                      </div>
                      <div class="col-3">
                        <CampoInput id="valorTotalNegociadoGerencial" label="Valor Total" type="moeda" :model-value="item.valorTotalNegociadoGerencial" disabled />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </template>
        </div>
      </div>
    </div>

    <div v-if="isDocumentoEntradaVisivel">
      <DocumentoEntradaGrid
        ref="documentoEntradaGridRef"
        v-model:documento-selecionado="documentoSelecionado"
        :id-ordem-compra="item.idOrdemCompra"
        :lst-status-ordem-compra="item.lstStatusOrdemCompra"
        :tem-modulo-documento="temDocumentoEntrada"
        :tem-completo="temPermissaoCompleta"
        :tem-visualizacao="temPermissaoCompleta"
        :pode-editar="!isDocumentoEntradaReadOnly"
        :listas="listas"
        :id-filial="item.idFilial"
        :lst-moeda="item.lstMoedaPadrao"
        :id-operacao="item.idOperacao"
        :id-parceiro-negocio-emissor="item.idParceiroNegocioFornecedor"
        :is-interno="false"
        :id-organizacao="authStore.empresaAtiva"
        :com-smart-mode="true"
        :config-busca-lote-fabricacao="configBuscaLoteFabricacao"
        :id-parceiro-negocio-fornecedor="item.idParceiroNegocioFornecedor"
        :id-local-estoque-padrao="item.idLocalEstoque"
        :id-condicao-pagamento="item.idCondicaoPagamento"
        :lst-forma-pagamento="item.lstFormaPagamento"
        :lst-tipo-taxa-conversao="item.lstTipoTaxaConversao"
        :lst-tipos-local-estoque="[ESTQ_TIPO_LOCAL_ESTOCAGEM.ProprioInterno]"
        contexto-origem="ordemCompra"
        modo-alocacao="saldo"
        :habilitar-parceiro-produtor="false"
        :com-accordion="true"
        :accordion-aberto="accordionState.documentosEntrada"
        @update:accordion-aberto="accordionState.documentosEntrada = $event"
        @acao-completa="onAcaoDocumentoCompleta"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </div>
    <div v-else-if="isOrdemSaved" class="text-center text-muted py-4 mb-3">
      <i class="bi bi-info-circle me-2"></i>
      Documentos de entrada disponíveis apenas a partir do status Emitida.
    </div>
    <div v-else class="text-center text-muted py-4 mb-3">
      <i class="bi bi-info-circle me-2"></i>
      Salve a ordem primeiro para lançar documentos de entrada.
    </div>

  </FormularioCadastro>

  <ModalFormularioV2
    v-model="mostrarAuditoria"
    titulo="Histórico de Alterações"
    tamanho="xl"
    :cor-header="'#34495E'"
  >
    <AuditLogViewer
      embedded
      tabela="OrdemCompra"
      :chave-primaria="item.idOrdemCompra"
      :titulo-registro="`Ordem ${item.numeroOrdem || item.idOrdemCompra}`"
      contexto="tenant"
      :tabelas-filhas="['OrdemCompraItem']"
      chave-estrangeira="IdOrdemCompra"
      :dicionarios="{
        LstMoedaPadrao: listas.LstMoedaPadrao,
        LstMoedaGerencial: listas.LstMoedaGerencial,
        LstIncoterm: listas.LstIncoterm,
        LstStatusOrdemCompra: listas.LstStatusOrdemCompra,
        LstFormaPagamento: listas.LstFormaPagamento,
        LstTipoTaxaConversao: listas.LstTipoTaxaConversao,
        LstFonteCambio: listas.LstFonteCambio,
        LstTaxaCambioStatus: listas.LstTaxaCambioStatus,
        LstMotivoDesconto: listas.LstMotivoDesconto
      }"
      @fechar="mostrarAuditoria = false"
    />
  </ModalFormularioV2>

  <ConfirmationModal
    ref="confirmModal"
    title="Confirmar Ação"
    :message="mensagemConfirmacao"
    confirm-button-text="Confirmar"
    cancel-button-text="Cancelar"
    tipo="warning"
    @confirmed="onConfirmAcao"
  />

  <RelatorioPreviewModal
    :templates="templatesRelatorio"
    :dados-relatorio="dadosRelatorio"
    :visible="showRelatorioModal"
    :id-empresa="Number(authStore.empresaAtiva)"
    @fechar="showRelatorioModal = false"
    @show-notification="(p) => emit('show-notification', p)"
  />
</template>
